import { Application } from 'pixi.js-legacy';

export const LOGICAL_WIDTH = 1024;
export const LOGICAL_HEIGHT = 576;

/**
 * Create and configure Pixi application with proper scaling
 */
export async function createPixiApp(
  container: HTMLElement
): Promise<Application> {
  const app = new Application({
    width: LOGICAL_WIDTH,
    height: LOGICAL_HEIGHT,
    backgroundColor: 0x1a1a1a,
    resolution: Math.min(window.devicePixelRatio || 1, 2), // Cap DPR at 2x
    antialias: true,
  });

  // Append canvas to container
  container.appendChild(app.view as HTMLCanvasElement);

  // Setup scaling and letterboxing
  setupScaling(app, container);

  // Handle window resize
  window.addEventListener('resize', () => setupScaling(app, container));

  return app;
}

/**
 * Setup uniform scaling with letterboxing
 */
function setupScaling(app: Application, container: HTMLElement): void {
  const containerWidth = container.clientWidth;
  const containerHeight = container.clientHeight;

  // Calculate scale to fit (maintain aspect ratio)
  const scaleX = containerWidth / LOGICAL_WIDTH;
  const scaleY = containerHeight / LOGICAL_HEIGHT;
  const scale = Math.min(scaleX, scaleY);

  // Calculate scaled dimensions
  const scaledWidth = LOGICAL_WIDTH * scale;
  const scaledHeight = LOGICAL_HEIGHT * scale;

  // Center the canvas with letterboxing
  const canvas = app.view as HTMLCanvasElement;
  canvas.style.width = `${scaledWidth}px`;
  canvas.style.height = `${scaledHeight}px`;
  canvas.style.position = 'absolute';
  canvas.style.left = `${(containerWidth - scaledWidth) / 2}px`;
  canvas.style.top = `${(containerHeight - scaledHeight) / 2}px`;
}

/**
 * Convert screen coordinates to logical coordinates
 */
export function screenToLogical(
  app: Application,
  screenX: number,
  screenY: number
): { x: number; y: number } {
  const canvas = app.view as HTMLCanvasElement;
  const rect = canvas.getBoundingClientRect();
  const scale = rect.width / LOGICAL_WIDTH;

  return {
    x: (screenX - rect.left) / scale,
    y: (screenY - rect.top) / scale,
  };
}
